import { useState, useRef, useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

type InsightItem = { title: string; body: string };
export type InsightsPayload = {
  schema_version: 'insight.v2';
  insights: InsightItem[];
  next_steps?: string[];
  figure_caption?: string;
};

// Helper function to copy text to clipboard
async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

// Copy button component with enhanced feedback
function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);
  const { toast } = useToast();

  const handleCopy = async () => {
    await copyToClipboard(text);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Insights copied to clipboard",
      duration: 2000,
    });
    if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current);
    // @ts-ignore setTimeout returns number in browsers
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('h-7 w-7 p-0 opacity-60 hover:opacity-100 transition-opacity flex-shrink-0', className)}
      onClick={handleCopy}
      aria-label="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-4 w-4 text-emerald-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      <span className="sr-only" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span>
    </Button>
  );
}

export function InsightsPanel({ data, className }: { data: InsightsPayload; className?: string }) {
  // Build full text content for copying
  const fullText = useMemo(() => {
    const parts: string[] = [];
    
    if (data.figure_caption) {
      parts.push(`Figure caption\n${data.figure_caption}`);
    }
    
    if (Array.isArray(data.insights) && data.insights.length > 0) {
      parts.push('\nResults');
      data.insights.forEach((it) => {
        parts.push(`${it.title}\n${it.body}`);
      });
    }
    
    if (Array.isArray(data.next_steps) && data.next_steps.length > 0) {
      parts.push('\nNext steps');
      data.next_steps.forEach((s, i) => {
        parts.push(`${i + 1}. ${s}`);
      });
    }
    
    return parts.join('\n\n');
  }, [data]);

  return (
    <div className={cn('space-y-6 relative', className)}>
      {/* Single copy button at the top right */}
      <div className="absolute top-0 right-0 z-10">
        <CopyButton text={fullText} />
      </div>

      {data.figure_caption && (
        <section>
          <h3 className="text-sm font-bold text-muted-foreground mb-1">Figure caption</h3>
          <p className="text-base leading-relaxed">{data.figure_caption}</p>
        </section>
      )}

      {Array.isArray(data.insights) && data.insights.length > 0 && (
        <section>
          <h3 className="text-sm font-bold text-muted-foreground mb-2">Results</h3>
          <div className="grid gap-3">
            {data.insights.map((it, i) => (
              <div key={i} className="rounded-2xl border p-3">
                <div className="font-medium">{it.title}</div>
                <p className="text-sm text-muted-foreground">{it.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(data.next_steps) && data.next_steps.length > 0 && (
        <section>
          <h3 className="text-sm font-bold text-muted-foreground mb-2">Next steps</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            {data.next_steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function isThirtyWords(s: string) {
  const words = String(s || '').trim().split(/\s+/).filter(Boolean);
  return words.length === 30;
}

